import React, {useCallback, useContext, useEffect, useMemo, useState} from 'react';
import dayjs from 'dayjs';
import {Alert, Avatar, Box, Button, Checkbox, Container, Card, CardContent, Chip,
  Divider, FormControlLabel, Stack, TextField, Typography} from '@mui/material';
import {Save, UploadFile, Person, ContactPhone, Home, Groups, Circle} from '@mui/icons-material';

import AnimationRevealPage from "components/AnimationRevealPage.js";
import Header from "components/Header.js";
import {fetchMyPlotImage, uploadMyPlotImage, updateProfile} from "helpers/userManagement.js";
import {storeLocal} from "helpers/utils.js";
import AuthContext from 'contexts/AuthContext';

const emptyForm = {
  name: '', firstName: '', lastName: '', phoneNumber: '',
  emergencyContact: '', emergencyPhone: '',
  street: '', sector: '', city: '', state: '', pinCode: '', country: '',
  isSelfOccupied: true, tenantName: '', tenantPhone: '', moveInDate: null
};

// A section of related fields, rendered as its own card with an icon-led
// heading. Fields reflow with the available width instead of being pinned
// to fixed breakpoints.
function FormSection({icon, title, children}) {
  return (
    <Card variant="outlined" sx={{borderRadius: 3}}>
      <CardContent sx={{p: 3}}>
        <Stack direction="row" alignItems="center" spacing={1.25} sx={{mb: 2.5}}>
          {icon}
          <Typography variant="subtitle1" sx={{fontFamily: 'Inter, sans-serif', fontWeight: 600}}>
            {title}
          </Typography>
        </Stack>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: 2.5
        }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
}

export default function Profile() {
  const {role, user, setUser} = useContext(AuthContext);
  const inputFile = React.useRef(null);

  const [plotImageUrl, setPlotImageUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [profileForm, setProfileForm] = useState(emptyForm);

  const hasPermission = useMemo(() => (role && role !== "USER" && role !== "REGISTER"), [role]);
  const hasPlot = useMemo(() => !!user?.plotNumber, [user]);
  const initials = useMemo(() => {
    const parts = (user?.name || '').trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    return (parts[0][0] + (parts[parts.length - 1][0] || '')).toUpperCase();
  }, [user]);

  useEffect(() => {
    if (user) {
      setProfileForm({
        name: user.name || '',
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phoneNumber: user.phoneNumber || '',
        emergencyContact: user.emergencyContact || '',
        emergencyPhone: user.emergencyPhone || '',
        street: user.street || '',
        sector: user.sector || '',
        city: user.city || '',
        state: user.state || '',
        pinCode: user.pinCode || '',
        country: user.country || '',
        isSelfOccupied: user.isSelfOccupied ?? true,
        tenantName: user.tenantName || '',
        tenantPhone: user.tenantPhone || '',
        moveInDate: user.moveInDate || null
      });
    }
  }, [user]);

  const loadPlotImage = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchMyPlotImage();
      setPlotImageUrl(response?.data || '');
    } catch (err) {
      console.error('Error loading plot image:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasPermission) {
      loadPlotImage();
    }
  }, [loadPlotImage, hasPermission]);

  const handleFileSelected = useCallback(async (event) => {
    const file = event.target.files[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await uploadMyPlotImage(file);
      await loadPlotImage();
    } catch (err) {
      console.error('Error uploading plot image:', err);
      setError('Failed to upload plot image. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [loadPlotImage]);

  const handleFieldChange = useCallback((field) => (event) => {
    const value = event.target.value;
    setProfileForm(prev => ({...prev, [field]: value}));
  }, []);

  const handleSelfOccupiedChange = useCallback((event) => {
    const checked = event.target.checked;
    setProfileForm(prev => ({...prev, isSelfOccupied: checked}));
  }, []);

  const handleSaveProfile = useCallback(async () => {
    if (!profileForm.name.trim()) {
      setError('Name is required.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const request = {
        email: user.email,
        name: profileForm.name,
        firstName: profileForm.firstName,
        lastName: profileForm.lastName,
        phoneNumber: profileForm.phoneNumber,
        emergencyContact: profileForm.emergencyContact,
        emergencyPhone: profileForm.emergencyPhone,
        street: profileForm.street,
        sector: profileForm.sector,
        city: profileForm.city,
        state: profileForm.state,
        pinCode: profileForm.pinCode,
        country: profileForm.country,
        isSelfOccupied: profileForm.isSelfOccupied,
        tenantName: profileForm.tenantName,
        tenantPhone: profileForm.tenantPhone,
        moveInDate: profileForm.moveInDate ? dayjs(profileForm.moveInDate).toISOString() : undefined
      };
      const res = await updateProfile(request);
      if (!res?.user) {
        throw new Error('Failed to update profile');
      }
      setUser(res.user);
      storeLocal('user', res.user);
      setSuccess('Profile updated successfully.');
    } catch (err) {
      console.error('Error updating profile:', err);
      setError('Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  }, [profileForm, user, setUser]);

  if (!hasPermission) {
    return (
      <AnimationRevealPage>
        <Header />
        <Container>
          <Alert severity="error">Permission denied</Alert>
        </Container>
      </AnimationRevealPage>
    );
  }

  return (
    <AnimationRevealPage>
      <Header />
      <Container maxWidth="xl" sx={{px: {xs: 2, md: 4}, py: {xs: 3, md: 5}}}>
        {error && (
          <Alert severity="error" sx={{mb: 3}} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{mb: 3}} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        {/* Page header: identity strip on the left, primary action on the right */}
        <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
          alignItems={{xs: 'flex-start', sm: 'center'}} spacing={2} sx={{mb: 4}}>
          <Stack direction="row" alignItems="center" spacing={2.5}>
            <Avatar sx={{width: 64, height: 64, bgcolor: 'primary.main',
              fontFamily: 'Inter, sans-serif', fontWeight: 700, fontSize: '1.25rem'}}>
              {initials}
            </Avatar>
            <Box>
              <Stack direction="row" alignItems="center" spacing={1.25}>
                <Typography variant="h5" sx={{fontFamily: 'Inter, sans-serif', fontWeight: 700}}>
                  {user?.name || 'My Profile'}
                </Typography>
                {role && (
                  <Chip label={role} size="small" color="primary" variant="outlined" />
                )}
              </Stack>
              <Typography color="text.secondary" sx={{mt: 0.25}}>
                {user?.email}
                {user?.plotNumber && (
                  <>
                    {' '}<Circle sx={{fontSize: 4, verticalAlign: 'middle', mx: 1}} />{' '}
                    Plot {user.plotNumber}
                  </>
                )}
              </Typography>
            </Box>
          </Stack>
          <Button variant="contained" size="large" startIcon={<Save />}
            onClick={handleSaveProfile} disabled={saving}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </Stack>

        {/* Main layout: form sections on the left, plot photo pinned on the right */}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {xs: '1fr', md: '2fr 1fr'},
          gap: 3,
          alignItems: 'start'
        }}>
          <Stack spacing={3}>
            <FormSection icon={<Person color="primary" />} title="Personal details">
              <TextField label="Full name" fullWidth required variant="outlined"
                value={profileForm.name} onChange={handleFieldChange('name')} />
              <TextField label="First name" fullWidth variant="outlined"
                value={profileForm.firstName} onChange={handleFieldChange('firstName')} />
              <TextField label="Last name" fullWidth variant="outlined"
                value={profileForm.lastName} onChange={handleFieldChange('lastName')} />
              <TextField label="Phone number" fullWidth variant="outlined"
                value={profileForm.phoneNumber} onChange={handleFieldChange('phoneNumber')} />
              <TextField label="Move-in date" type="date" fullWidth variant="outlined"
                InputLabelProps={{shrink: true}}
                value={profileForm.moveInDate ? dayjs(profileForm.moveInDate).format('YYYY-MM-DD') : ''}
                onChange={(e) => setProfileForm(prev => ({...prev,
                  moveInDate: e.target.value ? new Date(e.target.value) : null}))} />
            </FormSection>

            <FormSection icon={<ContactPhone color="primary" />} title="Emergency contact">
              <TextField label="Contact name" fullWidth variant="outlined"
                value={profileForm.emergencyContact} onChange={handleFieldChange('emergencyContact')} />
              <TextField label="Contact phone" fullWidth variant="outlined"
                value={profileForm.emergencyPhone} onChange={handleFieldChange('emergencyPhone')} />
            </FormSection>

            <FormSection icon={<Home color="primary" />} title="Home address">
              <TextField label="Street" fullWidth variant="outlined"
                value={profileForm.street} onChange={handleFieldChange('street')} />
              <TextField label="Sector" fullWidth variant="outlined"
                value={profileForm.sector} onChange={handleFieldChange('sector')} />
              <TextField label="City" fullWidth variant="outlined"
                value={profileForm.city} onChange={handleFieldChange('city')} />
              <TextField label="State" fullWidth variant="outlined"
                value={profileForm.state} onChange={handleFieldChange('state')} />
              <TextField label="Pin code" fullWidth variant="outlined"
                value={profileForm.pinCode} onChange={handleFieldChange('pinCode')} />
              <TextField label="Country" fullWidth variant="outlined"
                value={profileForm.country} onChange={handleFieldChange('country')} />
            </FormSection>

            <FormSection icon={<Groups color="primary" />} title="Occupancy">
              <Box sx={{gridColumn: '1 / -1'}}>
                <FormControlLabel
                  control={<Checkbox checked={profileForm.isSelfOccupied}
                    onChange={handleSelfOccupiedChange} />}
                  label="This home is self-occupied" />
              </Box>
              {!profileForm.isSelfOccupied && (
                <>
                  <TextField label="Tenant name" fullWidth variant="outlined"
                    value={profileForm.tenantName} onChange={handleFieldChange('tenantName')} />
                  <TextField label="Tenant phone" fullWidth variant="outlined"
                    value={profileForm.tenantPhone} onChange={handleFieldChange('tenantPhone')} />
                </>
              )}
            </FormSection>
          </Stack>

          <Box sx={{position: {md: 'sticky'}, top: {md: 24}}}>
            <Card variant="outlined" sx={{borderRadius: 3}}>
              <CardContent sx={{p: 3}}>
                <Typography variant="subtitle1"
                  sx={{fontFamily: 'Inter, sans-serif', fontWeight: 600, mb: 0.5}}>
                  My plot
                </Typography>
                <Typography color="text.secondary" variant="body2" sx={{mb: 2}}>
                  Plot number: {user?.plotNumber || 'Not assigned'}
                </Typography>

                {!hasPlot ? (
                  <Alert severity="info">
                    No plot number is on your account yet. Contact an administrator
                    before uploading a photo.
                  </Alert>
                ) : (
                  <>
                    <Divider sx={{mb: 2}} />
                    <Box
                      sx={{
                        width: '100%',
                        aspectRatio: '4 / 3',
                        borderRadius: 2,
                        border: '1px solid',
                        borderColor: 'divider',
                        backgroundColor: 'action.hover',
                        backgroundImage: plotImageUrl ? `url("${plotImageUrl}")` : 'none',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      {!loading && !plotImageUrl && (
                        <Typography color="text.secondary" variant="body2" align="center" sx={{px: 2}}>
                          No photo yet — upload one below
                        </Typography>
                      )}
                    </Box>
                    <input type="file" ref={inputFile} accept="image/*"
                      onChange={handleFileSelected} style={{display: 'none'}} />
                    <Button variant="outlined" fullWidth startIcon={<UploadFile />}
                      onClick={() => inputFile.current.click()} disabled={saving}
                      sx={{mt: 2}}>
                      {saving ? 'Uploading…' : (plotImageUrl ? 'Replace photo' : 'Upload photo')}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>
      </Container>
    </AnimationRevealPage>
  );
}
